import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTheme } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import GoogleMapReact from 'google-map-react';
import TextField from 'components/Input/TextField';
import Autocomplete from 'components/Input/Autocomplete';
import CircularProgress from 'components/ProgressLoader';
import useLocalStorage from 'hooks/useLocalStorage';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import MyLocationOutlined from '@material-ui/icons/MyLocationOutlined';
import { get, remove, debounce } from 'lodash';
import RecentSearch from '@material-ui/icons/YoutubeSearchedFor';
import DeleteOutlined from '@material-ui/icons/DeleteOutlined';
import Search from '@material-ui/icons/Search';
import Grid from '@material-ui/core/Grid';
import { Typography, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import parse from 'autosuggest-highlight/parse';

const { googleMapLoader } = GoogleMapReact;

// function loadScript(src, position, id) {
//   if (!position) {
//     return;
//   }

//   const script = document.createElement("script");
//   script.setAttribute("async", "");
//   script.setAttribute("id", id);
//   script.src = src;
//   position.appendChild(script);
// }

const autocompleteService = { current: null };

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
}));

function PlacesAutocomplete(props) {
  const classes = useStyles();
  const [autoCompleteValue, setAutoCompleteValue] = useState(props.defaultValue); //controlled autoComplete state
  const [inputStateValue, setInputValue] = useState(props.inputValue); //uncontrolled TextField input value (used to check conditions)
  const [recentLocationList, setRecentLocationList] = useLocalStorage(
    'recentSearchedPlaces',
    [],
  );
  const [loadingLatLng, setLoadingLatLng] = useState(false);
  const theme = useTheme();
  const themeOverrideProps = theme.props.PlacesAutocomplete || {};
  const {
    id = 'google-map-demo',
    APIkey: key = '',
    label: labelText = '',
    placeholder: placeholderText = '',
    variant = 'standard',
    background = 'light',
    disableUnderline = true,
    inputValue = inputStateValue,
    onInputChange = setInputValue,
    browserLocation = false,
    filterOptions = (x) => x,
    onChange = setAutoCompleteValue,
    value = autoCompleteValue,
    onSelect = () => {},
    localHistory = false,
    defaultOptions: defaultList,
    onRemoveDefaultOption = () => {},
    labels: {
      noOptionsText = 'No places found',
      openText = 'Type to search',
      geolocationText = 'Use my location',
    } = {},
    ...otherProps
  } = props;

  const [showResetOrLocation, setShowResetOrLocation] = useState(false); // controlled reset X button visiblity state
  const [isLoading, setLoading] = useState(false); //loading api results
  const [options, setOptions] = React.useState([]); // menu option list
  const [viewDefaultList, setViewDefaultList] = useState(false); //menu show in click and reveals default options
  const [myLocation, setMyLocation] = useState(null); //browser location

  // const loaded = React.useRef(false);

  let defaultOptions = useMemo(
    () => (localHistory ? recentLocationList : defaultList),
    [localHistory, recentLocationList, defaultList],
  );

  // if (typeof window !== "undefined" && !loaded.current) {
  //   if (!document.querySelector("#google-maps")) {
  //     loadScript(
  //       `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`,
  //       document.querySelector("head"),
  //       "google-maps"
  //     );
  //   }

  //   loaded.current = true;
  // }

  const handleInputChange = useCallback(
    (event) => {
      onInputChange(event.target.value);
    },
    [onInputChange],
  );

  const fetch = React.useMemo(
    () =>
      debounce((input = '', callback) => {
        autocompleteService.current.getPlacePredictions(input, callback);
      }, 400),
    [],
  );
  const saveRecentLocation = useCallback(
    (newList) => {
      let list = [...newList];
      if (newList.length > 10) {
        list = list.slice(0, 10);
      }
      setRecentLocationList(list);
    },
    [setRecentLocationList],
  );

  const getValuefromObject = (obj) => {
    return get(obj, 'place_id', null);
  };

  const handleLocationChange = useCallback(
    (e, value) => {
      const isNewValue =
        recentLocationList.findIndex((option) => {
          const optionValue = getValuefromObject(option);
          const AddedValue = getValuefromObject(value);
          if (optionValue && AddedValue) {
            return optionValue === AddedValue;
          }
          return false;
        }) === -1;

      if (isNewValue) {
        const newList = [value, ...recentLocationList];
        saveRecentLocation(newList);
      }
    },
    [recentLocationList, saveRecentLocation],
  );

  const handleRemoveDefaultOption = (id) => {
    if (localHistory) {
      var newList = remove(recentLocationList, function (item) {
        return getValuefromObject(item) !== id;
      });
      setRecentLocationList(newList);
    } else {
      onRemoveDefaultOption(id);
    }
  };

  const checkVisibleList = useCallback(() => {
    if (defaultOptions && defaultOptions.length && !inputValue) {
      setViewDefaultList(true);
      setOptions(defaultOptions);
    } else if (defaultOptions && !defaultOptions.length && !inputValue) {
      setViewDefaultList(false);
      setOptions(defaultOptions);
    }
  }, [defaultOptions, inputValue]);

  const onPopperOpen = useCallback(() => {
    checkVisibleList(); // default options already set on mount
  }, [checkVisibleList]);

  const onInputChangeCallback = (e, value, actionType) => {
    if (actionType === 'reset') {
      setShowResetOrLocation(true);
    }
    if (actionType === 'clear') {
      onInputChange('');
      setShowResetOrLocation(false);
      onChange(null);
      onSelect({});
    }
  };
  const getCountryInfo = useCallback((addressList) => {
    const countryItem = addressList.filter((item) => {
      const hasCountryType =
        item.types.findIndex((type) => type === 'country') > -1;
      return hasCountryType;
    })[0];
    if (countryItem) {
      return {
        country: countryItem.long_name,
        country_code: countryItem.short_name,
      };
    } else {
      return null;
    }
  }, []);

  const takeMyLocation = useCallback(
    (params) => {
      const { placeId, place, latLng } = params;
      const geoCodeQuery = latLng ? { latLng } : { placeId };
      setLoadingLatLng(true);
      var geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(geoCodeQuery, function (results, status) {
        if (status === 'OK') {
          const result = results[0];
          const countryInfo = getCountryInfo(result.address_components);
          if (latLng) {
            // when using browser location
            let value = get(result, 'formatted_address');
            onChange(value);
            onSelect({
              location: { lat: latLng.lat, lng: latLng.lng },
              place: value,
              country: countryInfo,
            });
          } else {
            const lat = result.geometry.location.lat();
            const lng = result.geometry.location.lng();
            onSelect({ location: { lat, lng }, place, country: countryInfo });
          }
          setLoadingLatLng(false);
        } else {
          window.alert('Geocoder failed due to: ' + status);
          setLoadingLatLng(false);
        }
      });
    },
    [onChange, onSelect, getCountryInfo],
  );

  const handleChange = useCallback(
    (e, value) => {
      onChange(value);
      value &&
        value.place_id &&
        takeMyLocation({ placeId: value.place_id, place: value.description });
      if (value && localHistory) {
        handleLocationChange(e, value);
      }
    },
    [onChange, takeMyLocation, handleLocationChange, localHistory],
  );

  useEffect(
    () => () => {
      onChange(null);
      onSelect({});
    },
    [],
  ); // clear values on unmount

  useEffect(() => {
    if (browserLocation) {
      navigator.geolocation.getCurrentPosition(function (position) {
        const lat = get(position, 'coords.latitude', null);
        const lng = get(position, 'coords.longitude', null);
        if (lat && lng) {
          setMyLocation({ lat, lng });
        }
      });
    } else {
      setMyLocation(null);
    }
  }, [browserLocation]); // on browserLocation change

  useEffect(() => {
    checkVisibleList();
  }, [checkVisibleList]);

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current) {
      // autocompleteService.current = new window.google.maps.places.AutocompleteService();
      googleMapLoader({
        key,
        libraries: ['places'].join(','),
      }).then((maps) => {
        autocompleteService.current = new maps.places.AutocompleteService();
        // use places
      });
    }

    if (!autocompleteService.current) {
      return undefined;
    }

    if (!inputValue) {
      setLoading(false);
      setViewDefaultList(true);
      setOptions(defaultOptions || []);
      return undefined;
    }
    setLoading(true);
    fetch({ input: inputValue }, (results) => {
      if (active) {
        setViewDefaultList(false);
        setOptions(results || []);
        setLoading(false);
      }
    });

    return () => {
      active = false;
    };
  }, [key, inputValue, setOptions, defaultOptions, fetch]);

  return (
    <Autocomplete
      id={id}
      style={{ minWidth: 120 }}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      filterOptions={filterOptions}
      background={background}
      options={isLoading ? [] : options}
      onInputChange={onInputChangeCallback}
      noOptionsText={
        inputValue
          ? themeOverrideProps.noOptionsText || noOptionsText
          : themeOverrideProps.openText || openText
      }
      onChange={handleChange}
      value={value}
      onOpen={onPopperOpen}
      autoComplete
      forcePopupIcon={false}
      disableClearable={myLocation ? !showResetOrLocation : false}
      includeInputInList
      disableOpenOnFocus={!viewDefaultList}
      loading={isLoading}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            label={themeOverrideProps.labelText || labelText}
            variant={variant}
            onChange={handleInputChange}
            placeholder={themeOverrideProps.placeholderText || placeholderText}
            {...otherProps}
            fullWidth
            InputProps={{
              ...params.InputProps,
              // startAdornment: <Search style={{ color: "#aaa" }} />,
              disableUnderline: disableUnderline,
              endAdornment: (
                <React.Fragment>
                  {loadingLatLng ? (
                    <CircularProgress
                      type="circular"
                      color="inherit"
                      size={20}
                    />
                  ) : null}
                  {myLocation && !showResetOrLocation ? (
                    <IconButton
                      size="small"
                      title={
                        themeOverrideProps.geolocationText || geolocationText
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        takeMyLocation({ latLng: myLocation });
                      }}
                    >
                      <MyLocationOutlined size={20} />
                    </IconButton>
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        );
      }}
      renderOption={(option) => {
        const id = option.place_id;
        const matches =
          option.structured_formatting.main_text_matched_substrings;
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length]),
        );
        let Icon = LocationOnIcon;
        if (viewDefaultList) {
          Icon = RecentSearch;
        }
        // console.log({
        //   place_id: option.place_id,
        //   main_text_matched_substrings:
        //     option.structured_formatting.main_text_matched_substrings,
        //   secondary_text: option.structured_formatting.secondary_text,
        //   main_text: option.structured_formatting.main_text
        // });

        return (
          <Grid container alignItems="center">
            <Grid item>
              <Icon className={classes.icon} />
            </Grid>
            <Grid item xs>
              {parts.map((part, index) => (
                <span
                  key={index}
                  style={{ fontWeight: part.highlight ? 700 : 400 }}
                >
                  {part.text}
                </span>
              ))}

              <Typography variant="body2" color="textSecondary">
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
            {viewDefaultList && (
              <Grid item>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveDefaultOption(id);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Grid>
            )}
          </Grid>
        );
      }}
    />
  );
}

PlacesAutocomplete.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
  disableUnderline: PropTypes.bool,
  multiline: PropTypes.bool,
  fullWidth: PropTypes.bool,
  id: PropTypes.string,
  APIkey: PropTypes.string.isRequired,
  label: PropTypes.string,
  variant: PropTypes.string,
  inputValue: PropTypes.string,
  onInputChange: PropTypes.func,
  browserLocation: PropTypes.bool,
  filterOptions: PropTypes.func,
  localHistory: PropTypes.bool,
  /**
   * for controlled default list pass value
   */
  defaultOptions: PropTypes.array,
  /**
   * for controlled default list pass callback
   */
  onRemoveDefaultOption: PropTypes.func,
};

export default PlacesAutocomplete;
